import React from 'react';
import { useFormikContext } from 'formik';
import uniqBy from 'lodash/uniqBy';
import moment from 'moment';
import { constants } from '@clubmed/utils';
import { FormikInput as FormField } from 'Components/frameworks/formik/FormikInput';
import { Title } from 'Components/frameworks/title/Title';
import { t } from 'Src/i18n';
type EventFormProps = {
  displayAddressFields: boolean;
  typeOptions: Array<{
    value: string;
    name: string;
  }>;
  regions: Array<{
    value: string;
    name: string;
  }>;
  resorts: Array<EventFormResort>;
  userSalutationOptions: Array<{
    value: string;
    name: string;
  }>;
};
const { OPENING_STATUS } = constants;
export const EventFormFields = ({
  displayAddressFields = true,
  typeOptions,
  regions,
  resorts,
  userSalutationOptions,
}: EventFormProps) => {
  const { values } = useFormikContext();
  const openedResorts = resorts.filter(
    ({ openingStatus }) => openingStatus === OPENING_STATUS.OPEN,
  );
  return (
    <React.Fragment>
      <Title className="mb-10 mt-30 px-10" hLevel={3} theme="title-sans-n4">
        {t('eventForm.packageSection.title')}
      </Title>
      <div className="flex flex-wrap">
        <div className="w-full p-10 md:w-1/2">
          <FormField
            dataTestId="event-type"
            isRequired
            label={t('eventForm.packageSection.eventTypeLabel')}
            name="type"
            options={uniqBy(typeOptions, 'value')}
            placeholder={t('eventForm.packageSection.eventTypePlaceholder')}
            type="select"
          />
        </div>
        <div className="w-full p-10 md:w-1/2">
          <FormField
            dataTestId="event-participants"
            isRequired
            label={t('eventForm.packageSection.numberOfParticipantsLabel')}
            min={1}
            name="numberOfParticipants"
            optimized
            placeholder={t('eventForm.packageSection.numberOfParticipantsPlaceholder')}
            type="number"
          />
        </div>
      </div>
      <Title className="mb-10 mt-30 px-10" hLevel={3} theme="title-sans-n4">
        {t('eventForm.staySection.title')}
      </Title>
      <div className="flex flex-wrap">
        <div className="w-full md:w-1/2">
          <div className="w-full p-10">
            <FormField
              dataTestId="event-region"
              label={t('eventForm.staySection.destinationLabel')}
              name="region"
              options={regions}
              placeholder={t('eventForm.staySection.destinationPlaceholder')}
              type="select"
            />
          </div>
          <div className="w-full p-10">
            <FormField
              dataTestId="event-resort"
              label={t('eventForm.staySection.resortLabel')}
              name="resort"
              options={openedResorts
                .filter((resort) => !values.region || resort.regionId === values.region)
                .map((resort) => ({
                  name: resort.name,
                  value: resort.id,
                }))}
              placeholder={t('eventForm.staySection.resortPlaceholder')}
              type="select"
            />
          </div>
        </div>
        <div className="w-full p-10 md:w-1/4">
          <FormField
            className="z-1"
            isDateRange
            label={t('eventForm.staySection.datesLabel')}
            minDate={moment()
              .add({
                days: 1,
              })
              .toDate()}
            name="dates"
            placeholder={t('eventForm.staySection.datesPlaceholder')}
            type="datepicker"
          />
        </div>
        <div className="w-full p-10 md:w-1/4">
          <FormField
            className="mb-10 md:mt-30"
            dataTestId="event-flexible-dates"
            label={t('eventForm.staySection.flexibleDatesLabel')}
            name="withFlexibleDates"
            type="checkbox"
          />
        </div>
        <p className="mb-10 w-full px-10 pt-10 text-14 font-bold">
          {t('eventForm.packageSection.packageServicesSection.packageServicesLabel')}
        </p>
        <div className="flex w-full flex-wrap md:w-1/2">
          <div className="w-full p-10 md:w-1/2">
            <FormField
              className="mb-10"
              dataTestId="event-with-rooms"
              label={t('eventForm.packageSection.packageServicesSection.roomsCheckboxLabel')}
              name="withRooms"
              optimized
              type="checkbox"
            />
            <FormField
              dataTestId="event-number-rooms"
              hideErrorMessage
              isDisabled={!values.withRooms}
              isRequired={values.withRooms}
              label={t('eventForm.packageSection.packageServicesSection.roomsInputLabel')}
              min={1}
              name="numberOfRooms"
              placeholder={t(
                'eventForm.packageSection.packageServicesSection.roomsInputPlaceholder',
              )}
              type="number"
            />
          </div>
          <div className="w-full p-10 md:w-1/2">
            <FormField
              className="mb-10"
              label={t(
                'eventForm.packageSection.packageServicesSection.transportationCheckboxLabel',
              )}
              name="withTransport"
              optimized
              type="checkbox"
            />
            <FormField
              isDisabled={!values.withTransport}
              label={t('eventForm.packageSection.packageServicesSection.transportationInputLabel')}
              maxLength={255}
              name="departureCity"
              placeholder={t(
                'eventForm.packageSection.packageServicesSection.transportationInputPlaceholder',
              )}
              type="text"
            />
          </div>
          <div className="w-full p-10 md:w-1/2">
            <FormField
              className="mb-10"
              label={t('eventForm.packageSection.packageServicesSection.meetingRoomsCheckboxLabel')}
              name="withMeetingRooms"
              optimized
              type="checkbox"
            />
          </div>
        </div>
        <div className="w-full p-10 md:w-1/2">
          <FormField
            className="mb-10"
            label={t('eventForm.packageSection.packageServicesSection.otherServicesCheckboxLabel')}
            name="withServices"
            optimized
            type="checkbox"
          />
          <FormField
            isDisabled={!values.withServices}
            label={t('eventForm.packageSection.packageServicesSection.otherServicesInputLabel')}
            name="logisticsComment"
            maxLength={255}
            placeholder={t(
              'eventForm.packageSection.packageServicesSection.otherServicesInputPlaceholder',
            )}
            type="textarea"
          />
        </div>
      </div>
      <Title className="mb-10 mt-30 px-10" hLevel={3} theme="title-sans-n4">
        {t('eventForm.userSection.title')}
      </Title>
      <div className="flex flex-wrap">
        <div className="w-full">
          <div className="w-full p-10 md:w-1/2">
            <FormField
              dataTestId="event-owner-company"
              label={t('eventForm.userSection.companyLabel')}
              maxLength={255}
              name="company"
              optimized
              placeholder={t('eventForm.userSection.companyPlaceholder')}
              type="text"
            />
          </div>
        </div>
        <div className="w-1/2 p-10 md:w-1/6">
          <FormField
            dataTestId="event-owner-salutation"
            isRequired
            label={t('eventForm.userSection.salutationLabel')}
            name="salutation"
            optimized
            options={userSalutationOptions}
            placeholder={t('eventForm.userSection.salutationPlaceholder')}
            type="select"
          />
        </div>
        <div className="w-full p-10 md:w-1/3">
          <FormField
            dataTestId="event-owner-firstName"
            isRequired
            label={t('eventForm.userSection.firstNameLabel')}
            maxLength={255}
            name="firstName"
            optimized
            placeholder={t('eventForm.userSection.firstNamePlaceholder')}
            type="text"
          />
        </div>
        <div className="w-full p-10 md:w-1/2">
          <FormField
            dataTestId="event-owner-lastName"
            isRequired
            label={t('eventForm.userSection.lastNameLabel')}
            maxLength={255}
            name="lastName"
            optimized
            placeholder={t('eventForm.userSection.lastNamePlaceholder')}
            type="text"
          />
        </div>
        <div className="w-full p-10 md:w-1/2">
          <FormField
            dataTestId="event-owner-email"
            isRequired
            label={t('eventForm.userSection.emailLabel')}
            maxLength={255}
            name="email"
            optimized
            placeholder={t('eventForm.userSection.emailPlaceholder')}
            type="text"
          />
        </div>
        <div className="w-full p-10 md:w-1/2">
          <FormField
            dataTestId="event-owner-phone"
            isRequired
            label={t('eventForm.userSection.phoneLabel')}
            maxLength={255}
            name="phone"
            optimized
            placeholder={t('eventForm.userSection.phonePlaceholder')}
            type="text"
          />
        </div>
        {displayAddressFields && (
          <div className="flex w-full flex-wrap">
            <p className="mb-10 w-full px-10 pt-10 text-14 font-bold">
              {t('eventForm.userSection.addressLabel')}
            </p>
            <div className="w-full p-10 md:w-1/2">
              <FormField
                dataTestId="event-owner-address"
                isRequired
                label={t('eventForm.userSection.streetLabel')}
                maxLength={255}
                name="address"
                optimized
                placeholder={t('eventForm.userSection.streetPlaceholder')}
                type="text"
              />
            </div>
            <div className="w-1/2 p-10 md:w-1/6">
              <FormField
                dataTestId="event-owner-postcode"
                isRequired
                label={t('eventForm.userSection.postcodeLabel')}
                maxLength={255}
                name="postcode"
                optimized
                placeholder={t('eventForm.userSection.postcodePlaceholder')}
                type="text"
              />
            </div>
            <div className="w-full md:w-1/3">
              <div className="w-full p-10">
                <FormField
                  dataTestId="event-owner-city"
                  isRequired
                  label={t('eventForm.userSection.cityLabel')}
                  maxLength={255}
                  name="city"
                  optimized
                  placeholder={t('eventForm.userSection.cityPlaceholder')}
                  type="text"
                />
              </div>
              <div className="w-full p-10">
                <FormField
                  dataTestId="event-owner-country"
                  isRequired
                  label={t('eventForm.userSection.countryLabel')}
                  maxLength={255}
                  name="country"
                  optimized
                  placeholder={t('eventForm.userSection.countryPlaceholder')}
                  type="text"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <Title className="mb-10 mt-30 px-10" hLevel={3} theme="title-sans-n4">
        {t('eventForm.commentSection.title')}
      </Title>
      <div className="flex flex-wrap">
        <div className="w-full p-10">
          <FormField
            label={t('eventForm.commentSection.commentLabel')}
            maxLength={255}
            name="generalComment"
            placeholder={t('eventForm.commentSection.commentPlaceholder')}
            type="textarea"
          />
        </div>
        <div className="w-full p-10">
          <p className="mb-5">{t('eventForm.commentSection.optinLabel')}</p>
          <FormField
            className="mb-10"
            label={t('eventForm.commentSection.emailOptinLabel')}
            name="optinEmail"
            optimized
            type="checkbox"
          />
          <FormField
            className="mb-10"
            label={t('eventForm.commentSection.smsOptinLabel')}
            name="optinSMS"
            optimized
            type="checkbox"
          />
          <FormField
            className="mb-10"
            label={t('eventForm.commentSection.phoneOptinLabel')}
            name="optinPhone"
            optimized
            type="checkbox"
          />
        </div>
      </div>
    </React.Fragment>
  );
};
